import { useState } from 'react';

import {
  Box,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';

import BottomSheet from '@/designs-dev/device/BottomSheet';
import CircleMemberCard from '@/karehero-app/onboarding/CircleMemberCard';
import {
  type CircleMember,
  RECIPIENT_NAME,
  ROLE_PERMS,
} from '@/karehero-app/onboarding/Onboarding.content';

// Initials from the part of an address before the @, since the invited person
// puts their own name in when they accept.
const initialsFor = (email: string) =>
  email
    .split('@')[0]
    .split(/[.\-_+]+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

/**
 * Inviting somebody into the circle: an email and a relationship.
 *
 * Out of CareCircle so the intake asks the same way the circle does. `invited`
 * lists who has already gone, for a sheet opened a second time, and an address
 * already on it cannot be sent twice.
 */
const InviteSheet = ({
  invited = [],
  onSend,
  onClose,
}: {
  invited?: CircleMember[];
  onSend: (member: CircleMember) => void;
  onClose: () => void;
}) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('Family member');

  const address = email.trim();
  const already = invited.some(
    (m) => m.name.toLowerCase() === address.toLowerCase(),
  );

  const send = () => {
    if (!address.includes('@') || already) return;
    onSend({
      initials: initialsFor(address),
      name: address,
      // The relationship alone. The card's chip says they are invited.
      role,
      perms: { ...(ROLE_PERMS[role] ?? ROLE_PERMS.Other) },
    });
  };

  return (
    <BottomSheet variant='hug' showHandleClose={false} onClose={onClose}>
      <Stack sx={{ pb: 1 }} spacing={2}>
        <Box
          sx={{
            alignSelf: 'center',
            width: 36,
            height: 4,
            borderRadius: '2px',
            bgcolor: 'custom.neutral.300',
          }}
        />
        <Box>
          <Typography variant='h4'>
            Invite to {RECIPIENT_NAME}&rsquo;s care circle
          </Typography>
          <Typography sx={{ color: 'text.secondary' }} variant='body2'>
            They get an email invite, and access to the app.
          </Typography>
        </Box>
        {invited.length > 0 && (
          <Stack spacing={1.5}>
            {/* No label: the chip on each card already says invited. */}
            {invited.map((member) => (
              <CircleMemberCard key={member.name} member={member} />
            ))}
            <Divider />
          </Stack>
        )}
        <TextField
          slotProps={{ inputLabel: { shrink: true } }}
          label='Email'
          type='email'
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
        />
        <TextField
          slotProps={{ inputLabel: { shrink: true } }}
          select
          label='Relationship'
          value={role}
          onChange={(e) => setRole(e.target.value)}
          fullWidth
        >
          {Object.keys(ROLE_PERMS).map((r) => (
            <MenuItem key={r} value={r}>
              {r}
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant='primary'
          size='lg'
          isFullWidth
          ariaLabel={already ? 'Already invited' : 'Send invite'}
          isDisabled={!address.includes('@') || already}
          onPress={send}
        >
          {already ? 'Already invited' : 'Send invite'}
        </Button>
      </Stack>
    </BottomSheet>
  );
};

export default InviteSheet;
